<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ t("navigation.myAssignments") }}
            </h1>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
            ></div>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        {{ t("common.error") }}
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        {{ t("common.dismiss") }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Assignments List -->
        <div
            v-if="!loading && !error"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
            <div
                v-for="assignment in assignments"
                :key="assignment.id"
                class="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                @click="viewAssignment(assignment)"
            >
                <div class="p-6">
                    <div class="flex items-start justify-between mb-4">
                        <h3
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            {{ assignment.title }}
                        </h3>
                        <span
                            v-if="assignment.is_published"
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                        >
                            {{ t("assignments.published") }}
                        </span>
                        <span
                            v-else
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        >
                            {{ t("assignments.draft") }}
                        </span>
                    </div>

                    <p
                        v-if="assignment.description"
                        class="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2"
                    >
                        {{ assignment.description }}
                    </p>

                    <div class="space-y-2 text-sm">
                        <div
                            class="flex items-center text-gray-600 dark:text-gray-400"
                        >
                            <FileText
                                class="h-4 w-4 mr-2"
                                :class="{
                                    'text-primary-600 dark:text-primary-400':
                                        assignment.has_attachment,
                                    'text-gray-400 dark:text-gray-500':
                                        !assignment.has_attachment,
                                }"
                            />
                            <span
                                :class="{
                                    'text-primary-600 dark:text-primary-400':
                                        assignment.has_attachment,
                                    'text-gray-400 dark:text-gray-500':
                                        !assignment.has_attachment,
                                }"
                            >
                                {{
                                    assignment.has_attachment
                                        ? t("assignments.hasAttachment")
                                        : t("assignments.noAttachment")
                                }}
                            </span>
                        </div>

                        <div
                            class="flex items-center text-gray-600 dark:text-gray-400"
                        >
                            <CheckCircle2
                                class="h-4 w-4 mr-2"
                                :class="{
                                    'text-success-600 dark:text-success-400':
                                        assignment.submitted_at,
                                    'text-gray-400 dark:text-gray-500':
                                        !assignment.submitted_at,
                                }"
                            />
                            <span
                                :class="{
                                    'text-success-600 dark:text-success-400':
                                        assignment.submitted_at,
                                    'text-gray-400 dark:text-gray-500':
                                        !assignment.submitted_at,
                                }"
                            >
                                {{
                                    assignment.submitted_at
                                        ? t("assignments.submittedLabel")
                                        : t("assignments.notSubmitted")
                                }}
                            </span>
                        </div>

                        <div
                            v-if="assignment.submissions_count !== undefined"
                            class="flex items-center text-gray-600 dark:text-gray-400"
                        >
                            <FileText class="h-4 w-4 mr-2" />
                            <span>
                                {{
                                    t("assignments.submissionsCount", {
                                        count:
                                            assignment.submissions_count || 0,
                                    })
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div
            v-if="!loading && !error && assignments.length === 0"
            class="text-center py-12"
        >
            <FileText class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {{ t("assignments.noAssignments") }}
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {{ t("assignments.noAssignmentsDesc") }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { CheckCircle2, FileText } from "@lucide/vue";
import assignmentApi, { Assignment } from "@/api/assignment";

const router = useRouter();
const { t } = useI18n();

const assignments = ref<Assignment[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function fetchAssignments() {
    loading.value = true;
    error.value = null;

    try {
        assignments.value = await assignmentApi.getStudentAssignments();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || t("assignments.fetchFailed");
    } finally {
        loading.value = false;
    }
}

function clearError() {
    error.value = null;
}

function viewAssignment(assignment: Assignment) {
    router.push(`/student-assignments/${assignment.id}`);
}

onMounted(() => {
    fetchAssignments();
});
</script>
